<template>
  <Transition name="fade">
    <div
      v-if="isBannerVisible"
      class="fixed bottom-0 left-4 z-10 w-[calc(100%-1rem)] max-w-xl border-l-2 border-t-2 border-black bg-white text-black lg:bottom-10 lg:left-auto lg:right-10 lg:max-w-5xl"
    >
      <div class="relative flex flex-col justify-between px-5 py-5 md:px-8 lg:flex-row">
        <div class="mb-5 flex-1 text-sm lg:mb-0 lg:pr-6">
          <div class="mb-2 text-lg font-semibold">We value your privacy!</div>
          <p>
            By continuing to use our site, you agree to our use of cookies. For more information,
            please review our
            <NuxtLink :to="'/privacy'" class="underline hover:no-underline" no-prefetch>
              Privacy Policy </NuxtLink
            >.
          </p>
        </div>
        <div class="flex w-full flex-wrap justify-end lg:w-fit lg:grow-0 lg:self-end">
          <div
            class="flex w-full items-center justify-end gap-3 md:gap-4 lg:w-fit lg:grow-0 lg:gap-6"
          >
            <div class="flex-2 w-full">
              <button
                class="text-sm underline hover:no-underline md:text-base"
                type="button"
                @click="showPreferences = true"
              >
                Preferences
              </button>
            </div>
            <div>
              <Button
                class="!w-full md:!w-fit"
                variant="outline"
                :label="'Reject'"
                size="sm"
                @click="onDecline"
              />
            </div>
            <div>
              <Button class="!w-full md:!w-fit" :label="'Accept'" size="sm" @click="onAccept" />
            </div>
          </div>
        </div>
      </div>
      <Modal
        :show="showPreferences"
        class="max-w-[600px] text-black"
        content-class="!px-4 !py-6"
        variant="fullscreen"
        @on-close="() => (showPreferences = false)"
      >
        <div class="mb-4 text-lg font-semibold">Cookie Preferences</div>
        <p class="mb-12">
          By continuing to use our site, you agree to our use of cookies. For more information,
          please review our
          <NuxtLink
            :to="'/privacy'"
            class="underline hover:no-underline"
            no-prefetch
            @click="() => (showPreferences = false)"
          >
            Privacy Policy </NuxtLink
          >.
        </p>
        <p class="mb-6">Manage Cookies</p>
        <div class="mb-12 flex flex-col gap-4">
          <div v-for="item in preferences" :key="item.type" class="relative flex gap-2">
            <FormToggle
              v-model="state[item.type]"
              :name="item.type"
              :label="item.title"
              :disabled="item.isRequired"
            />
          </div>
        </div>
        <Button :label="'Save and Close'" @click="onSave" />
      </Modal>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { useGtm } from '@gtm-support/vue-gtm'
import { LocalStorageKey } from '~/types/localStorage'

enum CookieType {
  Necessary = 'necessary',
  Analytics = 'analytics',
  Advertising = 'advertising'
}

const route = useRoute()

const isBannerVisible = ref(false)
const showPreferences = ref(false)

const preferences = ref([
  {
    type: CookieType.Necessary,
    title: 'Strictly Necessary Cookies',
    description:
      'Write information about strictly necessary cookies here for the user to learn about this term and what it means.',
    isRequired: true
  },
  {
    type: CookieType.Analytics,
    title: 'Analytics Cookies',
    description:
      'Write information about analytics cookies here for the user to learn about this term and what it means.'
  },
  {
    type: CookieType.Advertising,
    title: 'Advertising Cookies',
    description:
      'Write information about advertising cookies here for the user to learn about this term and what it means.'
  }
])

const result = ref('Waiting for user interaction...')

const state = reactive({
  [CookieType.Necessary]: true,
  [CookieType.Analytics]: false,
  [CookieType.Advertising]: false
})

const gtm = useGtm()

const onDecline = () => {
  const accepted = [CookieType.Necessary]
  result.value = `User accepted the following cookies 📋: ${accepted.join(', ')}`
  localStorage.setItem(LocalStorageKey.CookieConsent, JSON.stringify(accepted))
  isBannerVisible.value = false
  gtm?.enable(false)

  console.log(result.value)
}

const onAccept = () => {
  const accepted = Object.values(CookieType)
  result.value = `User accepted all cookies. 👍: ${accepted.join(', ')}`
  localStorage.setItem(LocalStorageKey.CookieConsent, JSON.stringify(accepted))
  allConsentGranted()
  gtm?.enable(true)
  gtm?.trackView(route.name as string, route.path)

  isBannerVisible.value = false

  console.log(result.value)
}

const onSave = () => {
  const accepted = Object.values(CookieType).filter((type) => state[type])
  result.value = 'User accepted the following cookies 📋: ' + accepted.join(', ')
  localStorage.setItem(LocalStorageKey.CookieConsent, JSON.stringify(accepted))

  showPreferences.value = false
  isBannerVisible.value = false

  if (accepted.includes(CookieType.Advertising) || accepted.includes(CookieType.Analytics)) {
    if (accepted.includes(CookieType.Advertising) && accepted.includes(CookieType.Analytics)) {
      allConsentGranted()
    } else if (accepted.includes(CookieType.Analytics)) {
      analyticsConsentGranted()
    } else if (accepted.includes(CookieType.Advertising)) {
      advertisingConsentGranted()
    }
    gtm?.enable(true)
    gtm?.trackView(route.name as string, route.path)
  } else {
    gtm?.enable(false)
  }

  console.log(result.value)
}

function allConsentGranted() {
  gtag('consent', 'update', {
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    ad_storage: 'granted',
    analytics_storage: 'granted'
  })
}

function analyticsConsentGranted() {
  gtag('consent', 'update', {
    analytics_storage: 'granted'
  })
}

function advertisingConsentGranted() {
  gtag('consent', 'update', {
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    ad_storage: 'granted'
  })
}

onMounted(() => {
  useTimeoutFn(() => {
    isBannerVisible.value = !localStorage.getItem(LocalStorageKey.CookieConsent)

    if (
      localStorage.getItem(LocalStorageKey.CookieConsent)?.includes(CookieType.Analytics) ||
      localStorage.getItem(LocalStorageKey.CookieConsent)?.includes(CookieType.Advertising)
    ) {
      if (localStorage.getItem(LocalStorageKey.CookieConsent)?.includes(CookieType.Analytics)) {
        analyticsConsentGranted()
      }
      if (localStorage.getItem(LocalStorageKey.CookieConsent)?.includes(CookieType.Advertising)) {
        advertisingConsentGranted()
      }

      gtm?.enable(true)
      gtm?.trackView(route.name as string, route.path)
    }
  }, 1000)
})
</script>
